import { HttpError, HttpResult } from "@/types/http";

function getHttpRequestError(
  response: Response,
  json?: HttpError | { error: HttpError }
) {
  if (json && "error" in json) json = json.error;
  return {
    status: json?.status ?? response.status,
    statusText: json?.statusText ?? response.statusText,
    message: json?.message ?? response.statusText,
  };
}

export async function httpRequest<T>(
  url: string,
  request: RequestInit,
  includeResponseHeaders: boolean = false
): Promise<HttpResult<T>> {
  const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const { body, credentials = "omit", headers = {}, method = "GET" } = request;

  let response: Response;

  try {
    response = await fetch(url, {
      method,
      body,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      credentials,
    });
  } catch (networkErr) {
    // fetch failed entirely (DNS, CORS, offline, etc.)
    console.error(`Network error while requesting ${url}:`, networkErr);
    throw new HttpError(0, "Network error", (networkErr as Error).message);
  }

  const responseHeaders = includeResponseHeaders
    ? Object.fromEntries(response.headers.entries())
    : undefined;

  let text: string | undefined;
  try {
    text = await response.text();
  } catch {
    /* no body */
  }

  let json = undefined;

  if (text) {
    try {
      json = JSON.parse(text);
    } catch (parseErr) {
      throw new HttpError(
        response.status,
        "Invalid JSON response",
        (parseErr as Error).message
      );
    }
  }

  // Throw on HTTP errors or backend error object
  if (!response.ok) {
    const { message, status, statusText } = getHttpRequestError(
      response,
      json as HttpError | undefined
    );
    throw new HttpError(status, message, statusText);
  }

  return {
    data: json.data ?? json ?? null,
    headers: responseHeaders,
  };
}
